import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faToggleOff,
  faToggleOn,
  faTrash,
  faPlus,
  faStar,
  faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../components/Admin/AdminActionButton';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import Dialogs from '../../../components/Common/Dialog';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import CountryForm from './CountryForm';
import {
  CountryAdminModel,
  getAdminCountries,
  saveCountry,
  deleteCountry,
  setDefaultCountry,
  toggleCountryStatus,
  reorderCountries,
} from './services/Country.service';

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

interface SortableRowProps {
  country: CountryAdminModel;
  onEdit: (country: CountryAdminModel) => void;
  onDelete: (id: string) => void;
  onStatusPrompt: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  country,
  onEdit,
  onDelete,
  onStatusPrompt,
  onSetDefault,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: country.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#e0f2fe' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-gray-200">
      <td className="py-2 px-3 text-gray-400 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripVertical} />
      </td>
      <td className="text-left py-2 px-4 border-b font-medium">{country.name}</td>
      <td className="text-left py-2 px-4 border-b font-mono text-xs">{country.code}</td>
      <td className="text-left py-2 px-4 border-b">
        <AdminActionButton
          icon={faStar}
          title={country.isDefault ? 'Default country' : 'Set as default'}
          className={country.isDefault ? 'text-yellow-400' : 'text-gray-300'}
          hoverColor="hover:text-yellow-400"
          onClick={() => !country.isDefault && onSetDefault(country.id)}
        />
      </td>
      <td className="text-left py-2 px-4 border-b">
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            country.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {country.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="text-left py-4 px-4 border-b">
        <div className="flex gap-3">
          <AdminActionButton
            icon={faPenToSquare}
            title="Edit"
            hoverColor="hover:text-blue-600"
            onClick={() => onEdit(country)}
          />
          <AdminActionButton
            icon={country.isActive ? faToggleOn : faToggleOff}
            title={country.isActive ? 'Deactivate' : 'Activate'}
            className={country.isActive ? 'text-storygreen' : 'text-red-500'}
            hoverColor={country.isActive ? 'hover:text-green-700' : 'hover:text-red-700'}
            onClick={() => onStatusPrompt(country.id)}
          />
          <AdminActionButton
            icon={faTrash}
            title="Delete"
            hoverColor="hover:text-red-600"
            onClick={() => onDelete(country.id)}
          />
        </div>
      </td>
    </tr>
  );
};

const Countries = () => {
  const [countries, setCountries] = useState<CountryAdminModel[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Partial<CountryAdminModel> | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'status' | 'delete'>('status');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const reorderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const loadCountries = useCallback(
    (search?: string, active?: string) => {
      setLoading(true);
      getAdminCountries(
        (data) => {
          setCountries(data);
          setLoading(false);
        },
        () => {},
        undefined,
        search ?? (searchText || undefined),
        active ?? activeFilter,
      );
    },
    [searchText, activeFilter],
  );

  useEffect(() => {
    loadCountries('', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (text: string) => {
    setSearchText(text);
    loadCountries(text, activeFilter);
  };

  const handleActiveFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setActiveFilter(val);
    loadCountries(searchText, val);
  };

  const handleSave = async (data: Partial<CountryAdminModel>) => {
    await saveCountry(data, () => {
      setShowForm(false);
      setEditingCountry(null);
      loadCountries(searchText, activeFilter);
    });
  };

  const handleEdit = (country: CountryAdminModel) => {
    setEditingCountry(country);
    setShowForm(true);
  };

  const handleDeletePrompt = (id: string) => {
    setSelectedId(id);
    setDialogAction('delete');
    setOpenDialog(true);
  };

  const handleStatusPrompt = (id: string) => {
    setSelectedId(id);
    setDialogAction('status');
    setOpenDialog(true);
  };

  const handleDialogConfirm = async () => {
    if (!selectedId) return;
    if (dialogAction === 'delete') {
      await deleteCountry(selectedId, () => loadCountries(searchText, activeFilter));
    } else {
      await toggleCountryStatus(selectedId, () => loadCountries(searchText, activeFilter));
    }
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultCountry(id, () => loadCountries(searchText, activeFilter));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = countries.findIndex((c) => c.id === active.id);
    const newIndex = countries.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(countries, oldIndex, newIndex);
    setCountries(reordered);

    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(() => {
      reorderCountries(reordered.map((c) => c.id));
    }, 800);
  };

  const columns = [
    { key: 'drag', label: '' },
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'ISO Code' },
    { key: 'isDefault', label: 'Default' },
    { key: 'isActive', label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <div className="p-0 md:p-4">
      {showForm ? (
        <CountryForm
          setShowForm={setShowForm}
          countryDetails={editingCountry}
          onSave={handleSave}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex justify-between items-center flex-wrap gap-2 my-5">
            <div className="flex items-center gap-2">
              <AdminSearchInput onSearch={handleSearch} placeholder="Search by name or code..." className="w-64" />
              <select
                value={activeFilter}
                onChange={handleActiveFilter}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {ACTIVE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingCountry(null);
                  setShowForm(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-storyblue text-white rounded-md hover:shadow-md transition-shadow"
              >
                <FontAwesomeIcon icon={faPlus} />
                New Country
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <AdminTableHeader columns={columns} />
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={countries.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                    <tbody>
                      {countries.length > 0 ? (
                        countries.map((country) => (
                          <SortableRow
                            key={country.id}
                            country={country}
                            onEdit={handleEdit}
                            onDelete={handleDeletePrompt}
                            onStatusPrompt={handleStatusPrompt}
                            onSetDefault={handleSetDefault}
                          />
                        ))
                      ) : (
                        <NoRecordFound colSpan={6} />
                      )}
                    </tbody>
                  </SortableContext>
                </DndContext>
              </table>
            </div>
          )}

          <Dialogs
            openDialog={openDialog}
            handelClose={() => setOpenDialog(false)}
            handelProcess={handleDialogConfirm}
            confirmMessage={
              dialogAction === 'delete'
                ? 'Are you sure you want to delete this country? This action cannot be undone.'
                : 'Are you sure you want to change the status of this country?'
            }
          />
        </>
      )}
    </div>
  );
};

export default Countries;
